import React, { memo, ReactEventHandler, useEffect, useMemo } from 'react';
import Animate from 'rc-animate';
import classNames from 'classnames';
import './primaryExamPoint.less';
import { Progress } from 'antd';
import { PrimaryExamPointGoalScoreStar, PrimaryExamPointsCollapseIcon } from '@/svgs';
import { useAppStore } from '@/store';
import SecondaryExamPoint from './SecondaryExamPoint';
import ChildExamPoint from './ChildExamPoint';
import { IHomePageExamSyllabusContentItem } from '@/types';
import { useCollapseContext } from '../../context/CollapseContext';
import { useAuthContext } from '../../context/AuthContext';

interface IProps {
    data: IHomePageExamSyllabusContentItem;
}

const PrimaryExamPoint = (props: IProps) => {
    const { themeStore } = useAppStore();
    const { theme } = themeStore;

    const { data } = props;
    const { authUtils } = useAuthContext();
    const { collapseState, dispatch } = useCollapseContext();

    const { passed: hasPredictionScore } = authUtils.checkPredictionScore({ showError: false });

    const isCollapsed = collapseState[data.contentId] ?? false; // 默认未折叠 (true: 展开  false: 折叠)

    const collapsible = !!data.contentChildren?.length; // 是否可折叠

    const predictScoreProgress = useMemo(() => {
        // 没有预测分时，默认展示10%进度
        if (!hasPredictionScore) {
            return 10;
        }
        if (data.totalScore === 0 || data.predictionScore === 0) {
            return 0;
        }
        return (data.predictionScore / data.totalScore) * 100;
    }, [hasPredictionScore, data.totalScore, data.predictionScore]);

    const isCompleted = useMemo(() => {
        return data.predictionScore >= data.goalScore;
    }, [data.goalScore, data.predictionScore]);

    const handleChangeCollapse: ReactEventHandler<HTMLElement> = event => {
        event.persist();
        if (!collapsible) {
            event.stopPropagation();
            return;
        }
        let target = event.target as HTMLElement; // 解决typescript eventTarget dataset报错
        let isStop = target.dataset.event === 'false';
        while (target && !isStop) {
            if (target.dataset?.event === 'false') {
                isStop = true;
            }
            target = target.parentNode as HTMLElement;
        }
        if (isStop) {
            event.stopPropagation();
            return;
        }

        const childLevelIds = data.contentChildren.map(item => item.contentId);
        dispatch({
            type: isCollapsed ? 'BATCH_EXPAND' : 'BATCH_COLLAPSE',
            ids: [data.contentId, ...childLevelIds],
        });
    };

    const isEveryChildExamPoint = useMemo(() => data.contentChildren?.every(item => item.knowledgeId) || false, [data.contentChildren]);

    return (
        <div
            className={classNames('sub-tiku__exam-point', 'sub-tiku__primary-exam-point', {
                'sub-tiku__exam-point-collapse-active': isCollapsed,
            })}
        >
            <div
                className={classNames('sub-tiku__exam-point-main sub-tiku__primary-exam-point-main', {
                    completed: isCompleted,
                })}
                style={{ cursor: collapsible ? 'pointer' : 'default' }}
                onClick={handleChangeCollapse}
            >
                <div className="sub-tiku__exam-point-meta">
                    <div className="sub-tiku__exam-point-meta-prefix">
                        {collapsible ? (
                            <PrimaryExamPointsCollapseIcon
                                className="sub-tiku__exam-point-collapse-icon"
                                rotate={isCollapsed ? 0 : 180}
                                color={theme.mainColor}
                            />
                        ) : null}
                    </div>
                    <div className="sub-tiku__exam-point-meta-content">
                        <div className="sub-tiku__exam-point-meta-title">
                            <span>{data.title}</span>
                        </div>
                    </div>
                </div>
                <div className="sub-tiku__exam-point-action">
                    {data.lastTimeStatus === 1 ? (
                        <div className="sub-tiku__study-tag">最近一次</div>
                    ) : data.submitStatus === 0 ? (
                        <div className="sub-tiku__study-tag">未交卷</div>
                    ) : null}
                    <div className="sub-tiku__study-score">
                        <span className="sub-tiku__study-score-em">{hasPredictionScore ? data.predictionScore : '--'}</span>/
                        {data.totalScore}分
                    </div>
                    <div className="sub-tiku__study-progress-bar">
                        <Progress showInfo={false} percent={predictScoreProgress} size={[160, 6]} />
                    </div>
                </div>
            </div>
            {data.contentChildren?.length ? (
                <Animate transitionAppear={false} component="" transitionName="collapse-animation">
                    {isCollapsed ? (
                        <div className="sub-tiku__exam-point-children">
                            {data.contentChildren.map((item, index) => {
                                const isShowIconLine = isEveryChildExamPoint
                                    ? {
                                          prefix: index !== data.contentChildren.length - 1,
                                          suffix: index !== 0,
                                      }
                                    : isEveryChildExamPoint;
                                return item.knowledgeId ? (
                                    <ChildExamPoint
                                        key={`sub-tiku__exam-point-${item.contentId}`}
                                        data={item}
                                        isShowIconLine={isShowIconLine}
                                    />
                                ) : (
                                    <SecondaryExamPoint key={`sub-tiku__exam-point-${item.contentId}`} data={item} />
                                );
                            })}
                        </div>
                    ) : null}
                </Animate>
            ) : null}
        </div>
    );
};

export default memo(PrimaryExamPoint);
